import React from 'react';
import { Link } from 'react-router-dom';
import { Icon, Popup } from 'semantic-ui-react';
import { Translate } from 'react-redux-i18n';
import { SidebarMenuItem as MenuItem } from '../../constants/sidebarMenuItems';
import { CustomIcon } from '../icons';
import NavigationStateManager from '../../helpers/NavigationStateManager';
import type { Session } from '../../modules/session';
import { SystemOnlyIcon, SystemOnlyPopup } from '../common/SystemOnlyBadge';

interface SidebarMenuItemProps {
  item: MenuItem;
  isActive: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  session: Session;
  activeSubRoute?: string | null;
  isSubItem?: boolean;
  isMinimized?: boolean;
}

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  item,
  isActive,
  isExpanded = false,
  onToggleExpanded,
  session,
  activeSubRoute,
  isSubItem = false,
  isMinimized = false
}) => {
  const isExternalLink = item.route.startsWith('http');
  const isSubItemActive = activeSubRoute === item.key;
  // The minimized sidebar hides labels and shows its own popup, so the mark waits for the open one.
  const showSystemOnly = !!item.systemOnly && !isMinimized;
  const describe = (link: React.ReactElement) =>
    showSystemOnly ? <SystemOnlyPopup>{link}</SystemOnlyPopup> : link;

  // Handle click to clear return URL for Settings navigation
  const handleClick = () => {
    NavigationStateManager.clearReturnUrl();
  };

  const MenuContent = () => (
    <div 
      className={`sidebar-menu-item ${(isActive || isSubItemActive) ? 'active' : ''} ${isSubItem ? 'sub-item' : ''}`}
    >
      <div className="sidebar-menu-item-content">
        {isSubItem ? (
          <div className="sidebar-menu-icon"></div>
        ) : (
          <CustomIcon name={item.icon} className="sidebar-menu-icon" />
        )}
        <span className="sidebar-menu-label">
          {item.label}
          {showSystemOnly && <SystemOnlyIcon />}
        </span>
        {/* Only show arrow on active items to indicate current page */}
        {(isActive || isSubItemActive) && !isMinimized && (
          <CustomIcon 
            name="chevron-right-01" 
            className="sidebar-menu-chevron"
            size={14}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="sidebar-menu-item-wrapper">
      {isMinimized ? (
        <Popup
          content={item.label}
          trigger={
            isExternalLink ? (
              <a 
                href={item.route} 
                target="_blank" 
                rel="noopener noreferrer"
                className="sidebar-menu-link"
              >
                <MenuContent />
              </a>
            ) : (
              <Link to={item.route} className="sidebar-menu-link" onClick={handleClick}>
                <MenuContent />
              </Link>
            )
          }
          position="right center"
          inverted
          size="mini"
          hoverable
        />
      ) : (
        isExternalLink ? (
          describe(
            <a
              href={item.route}
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-menu-link"
            >
              <MenuContent />
            </a>
          )
        ) : (
          describe(
            <Link to={item.route} className="sidebar-menu-link" onClick={handleClick}>
              <MenuContent />
            </Link>
          )
        )
      )}
    </div>
  );
};

export default SidebarMenuItem;
